import { Checkbox, Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from "@mui/material";
import { useEffect, useMemo, useState} from "react";
import { IGridcolumn } from "./interface/column.interface";
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import Pagination from '@mui/material/Pagination';
import { PageSizeSelector } from "./pageSizeSelector";
import { useTranslation } from 'react-i18next';
import { useDrag } from 'react-dnd';

export interface IProps {
    columns: IGridcolumn[];
    data: any;
    selection?: boolean;
    selectionChanged?: (id: string[]) => void;
    sortBy?: string;
    sortDirection?: 'ASC' | 'DESC' | '';
    sortChanged?: (column: string, direction: string) => void;
    pageChanged?: (page: number) => void;
    totalPages?: number;
    pageSizeChanged?: (size: number) => void;
    projectId?: string;
}
export const DataGrid = ({
    columns = [],
    data = [],
    selection = false,
    selectionChanged,
    sortChanged,
    sortBy = '',
    sortDirection = 'ASC',
    totalPages = 0,
    pageChanged,
    pageSizeChanged,
    projectId
}: IProps) => {
    const { t } = useTranslation();

    const [order, setOrder] = useState<string>('');
    const [sort, setSort] = useState<string>('');
    const [direction, setDirection] = useState<'ASC' | 'DESC' | ''>('ASC');
        const [itemsPerPage, setItemsPerPage] = useState<number>(() => {
        // Retrieve the persisted value from local storage or default to 20
        const savedPageSize = localStorage.getItem("itemsPerPage");
        return savedPageSize ? parseInt(savedPageSize, 10) : 20;
    });
    const [currentPage, setCurrentPage] = useState<number>(() => {
        // Retrieve the persisted value from local storage or default to 1
        const savedPage = localStorage.getItem("currentPage");
        return savedPage ? parseInt(savedPage, 10) : 1;
    });
    const [selected, setSelected] = useState<string[]>([]);

    useEffect(() => {
        const savedProjectId = localStorage.getItem("previousProjectId"); // Retrieve the previous projectId from localStorage
    
        if (projectId && savedProjectId !== projectId) {
            console.log('Project ID changed:', projectId);
    
            // Reset itemsPerPage and currentPage to default values
            setItemsPerPage(20);
            setCurrentPage(1);
            localStorage.setItem("itemsPerPage", "20");
            localStorage.setItem("currentPage", "1");

            // Remove filters + persistance from localStorage
            localStorage.removeItem("startDateFilter");
            localStorage.removeItem("stopDateFilter");
            localStorage.removeItem("filter");
            localStorage.removeItem("searchFilter");
            localStorage.removeItem("ScheduleritemsPerPage");
            localStorage.removeItem("SchedulerCurrentPage");
            localStorage.removeItem("SchedulerFilter");
            localStorage.removeItem("SchedulesBatchssCurrentPage");
            localStorage.removeItem("SchedulesBatchssitemsPerPage");
            localStorage.removeItem("SchedulesitemsPerPage");
            localStorage.removeItem("SchedulesCurrentPage");
            localStorage.removeItem("PreviousSchedulesId");
            localStorage.removeItem("ManualL1itemsPerPage");
            localStorage.removeItem("ManualL2currentPage");
            localStorage.removeItem("ManualL1currentPage");
            localStorage.removeItem("ManualL2itemsPerPage");
            localStorage.removeItem("ManualL1TestResultId");
            
            // Notify parent component about the page reset
            pageChanged && pageChanged(1);
            pageSizeChanged && pageSizeChanged(20);
    
            // Save the current projectId as the new previousProjectId
            localStorage.setItem("previousProjectId", projectId);
        }
    }, [projectId]); // Run this effect whenever projectId changes

    const handleItemsPerPageChange = (size: number) => {
        setItemsPerPage(size); // Update the state
        localStorage.setItem("itemsPerPage", size.toString()); // Persist the value in local storage
        pageSizeChanged && pageSizeChanged(size);
    }

    const RenderItem = ({ col = null as unknown as IGridcolumn, data = {} }) => {
        const Component = col?.redner;

        return (
            <Component  {...data} />
        )
    }

    const updateSort = (col: IGridcolumn) => () => {
        if (col.sortable && sortChanged) {
            if (sort === col.fieldName) {
                sortChanged(col.fieldName, direction === 'ASC' ? 'DESC' : 'ASC');
            } else {
                sortChanged(col.fieldName, 'ASC');
            }
            setSort(col.fieldName);

        }
    }

    const key = useMemo(() => columns.find(c => c.key)?.fieldName || 'id', [columns]);

    const checkChanged = (id: string) => (e: any) => {
        if (e.target.checked) {
            setSelected(selected.concat(id))
        } else {
            setSelected(selected.filter(s => s !== id));
        }
    }

    const onPageChanged = (e: any, page: number) => {
        setCurrentPage(page); // Update the state
        localStorage.setItem("currentPage", page.toString()); // Persist the value in local storage
      
        pageChanged && pageChanged(page)
        
    }

    useEffect(() => {
        setOrder(sortBy.substring(1));
        setDirection(sortBy.charAt(0) === '-' ? 'DESC' : 'ASC');
    }, [sortBy, sortDirection])

    useEffect(() => {
        setSelected([]);
    }, [data])


    useEffect(() => {
        selectionChanged && selectionChanged(selected);
    }, [selected])

    

    const ListItem = ({ ...props }) => {
        const [{ isDragging }, drag] = useDrag(() => ({
            type: 'TESTCASE-ROWS',
            item: {
                ...props.data
            },
            collect: (monitor: any) => ({
                isDragging: !!monitor.isDragging(),
            }),
        }))

        return (<TableRow style={{ height: '30px', padding: 0 }} className="relative" ref={drag}>{props.children}</TableRow>);
    }

    console.log('itemsPerPage:', itemsPerPage);
    console.log('currentPage:', currentPage);

    return (
        <Table>
            <TableHead>
                <TableRow>
                {selection && (
                    <TableCell width="55px">
                        <Checkbox
                        color="primary"
                        checked={selected.length === data.length && data.length > 0}
                        indeterminate={selected.length > 0 && selected.length < data.length}
                        onChange={(e) => {
                            if (e.target.checked) {
                            const allIds = data.map((row: any) => row[key]);
                            setSelected(allIds);
                            } else {
                            setSelected([]);
                            }
                        }}
                        />
                    </TableCell>
                    )}
                    {columns.map((col, colIndex) => (
                        <TableCell width={col.width || ''} key={`col_${colIndex}_${col.fieldName ?? col.caption ?? 'col'}`}>
                            <div onClick={updateSort(col)} className={`flex-center ${col.sortable ? 'pointer' : ''}`}>
                                {col.caption}

                                {col.fieldName === order && (
                                    direction === 'ASC' ?
                                        <ArrowDropUpOutlinedIcon />
                                        :
                                        <ArrowDropDownOutlinedIcon />
                                )}
                            </div>
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>

            <TableBody>
                {data.map((row: any, index: number) => (
                    <ListItem key={`row_${index}`} data={row}>                        
                        {selection && (
                            <TableCell>
                                <Checkbox color="primary" onChange={checkChanged(row[key])} checked={selected.indexOf(row[key]) !== -1} />
                            </TableCell>
                        )}
                        {columns.map((col, colIndex) => (
                            col.redner ? <TableCell key={`col_${index}_${colIndex}_${col.fieldName ?? col.caption ?? 'col'}`}><RenderItem col={col} data={row} /></TableCell> :
                                <TableCell key={`col_${index}_${colIndex}_${col.fieldName ?? col.caption ?? 'col'}`}>{row[col.fieldName]}</TableCell>
                        ))}
                    </ListItem>
                ))}
                {data?.length < 20 && (
                    Array.from(new Array(Math.max(1, 10 - (data?.length || 0)))).map((_, index) => (
                        <TableRow key={`extra_row_${index}`}>
                            <TableCell></TableCell>
                        </TableRow>
                    ))
                )}
            </TableBody>

            {totalPages >=0 && (
                <TableFooter >
                    <tr>
                        <td colSpan={(columns.length + (selected ? 1 : 0)) - 1}>
                            <Pagination onChange={onPageChanged} count={totalPages}  page={currentPage}  showFirstButton showLastButton />
                        </td>
                        <td align="right">
                            <PageSizeSelector handleItemsPerPageChange={handleItemsPerPageChange} itemsPerPage={itemsPerPage} ></PageSizeSelector>
                        </td>
                    </tr>
                </TableFooter>
            )}
        </Table>
    )
}